import {
  buildFrontloadingTutorAudioId,
  buildTermAudioId,
} from './LessonAudioManifest';

function mapLearningSteps(learning, sequenceIndex) {
  const rawSteps = Array.isArray(learning)
    ? learning
    : learning
    ? [learning]
    : [];

  return rawSteps.map((step, index) => ({
    phaseCode: step?.phaseCode || 'L',
    stepIndex: typeof step?.stepIndex === 'number' ? step.stepIndex : index,
    tutorScript: step?.tutorScript || '',
    tutorAudioId: buildTermAudioId(
      sequenceIndex,
      `learning[${index}].tutorScript`,
    ),
  }));
}

function mapWorkbookExercises(workbook, sequenceIndex) {
  const hasLegacyExerciseFields =
    workbook?.exercise ||
    workbook?.tutorReadExercise ||
    workbook?.tutorPresentOptions ||
    workbook?.correctAnswer ||
    (Array.isArray(workbook?.options) && workbook.options.length > 0);

  const rawExercises = Array.isArray(workbook?.exercises)
    ? workbook.exercises
    : hasLegacyExerciseFields
    ? [workbook]
    : [];

  return rawExercises.map((exercise, index) => ({
    exerciseIndex: index,
    exerciseNumber: exercise?.exerciseNumber || 0,
    exerciseLabel: exercise?.exerciseLabel || '',
    tutorReadExercise: exercise?.tutorReadExercise || '',
    tutorPresentOptions: exercise?.tutorPresentOptions || '',
    exercise: exercise?.exercise || '',
    options: exercise?.options || [],
    correctAnswer: exercise?.correctAnswer || '',
    optionFeedback: exercise?.optionFeedback || [],
    tutorTransition: exercise?.tutorTransition ?? null,
    isPrimary: exercise?.isPrimary ?? index === 0,
    tutorReadExerciseAudioId: buildTermAudioId(
      sequenceIndex,
      `workbook.exercises[${index}].tutorReadExercise`,
    ),
    tutorPresentOptionsAudioId: buildTermAudioId(
      sequenceIndex,
      `workbook.exercises[${index}].tutorPresentOptions`,
    ),
    tutorTransitionAudioId: exercise?.tutorTransition
      ? buildTermAudioId(
          sequenceIndex,
          `workbook.exercises[${index}].tutorTransition`,
        )
      : null,
    optionFeedbackAudioIds: (exercise?.optionFeedback || []).map(
      (_, feedbackIndex) =>
        buildTermAudioId(
          sequenceIndex,
          `workbook.exercises[${index}].optionFeedback[${feedbackIndex}]`,
        ),
    ),
  }));
}

function mapQuestion(q) {
  return {
    questionId: q.questionId ?? null,
    type: q.type,
    targetTerm: q.targetTerm,
    isCumulative: q.isCumulative ?? false,
    question: q.question,
    options: q.options ?? null,
    wordBank: q.wordBank ?? null,
    matchPairs: q.matchPairs ?? null,
    correctAnswer: q.correctAnswer,
    explanation: q.explanation ?? '',
    deferred: false,
  };
}

export function mapLessonContent(raw) {
  const content = raw.content || raw;
  const metadata = content.metadata || {};
  const termCards = content.termCards || [];
  const phases = content.phases || {};
  const frontloading = phases.frontloading || {};
  const termSequence = phases.termSequence || [];
  const masteryTest = phases.masteryTest || {};
  const guidedNotes = content.guidedNotes || {};

  const termOrder = frontloading.uiHighlightSequence || termCards.map(tc => tc.term);
  const termCardMap = Object.fromEntries(termCards.map(tc => [tc.term, tc]));
  const seqMap = Object.fromEntries(termSequence.map(ts => [ts.term, ts]));
  const seqIndexMap = Object.fromEntries(
    termSequence.map((ts, index) => [ts.term, index]),
  );

  const terms = termOrder.map(termName => {
    const card = termCardMap[termName] || {};
    const seq = seqMap[termName] || {};
    const wb = seq.workbook || {};
    const kc = seq.knowledgeCheck || {};
    const sequenceIndex =
      typeof seq.termIndex === 'number'
        ? seq.termIndex
        : seqIndexMap[termName] ?? 0;
    const learningSteps = mapLearningSteps(seq.learning, sequenceIndex);
    const primaryLearningStep = learningSteps[0] || {};
    const workbookExercises = mapWorkbookExercises(wb, sequenceIndex);
    const primaryWorkbookExercise =
      workbookExercises.find(exercise => exercise.isPrimary) ||
      workbookExercises[0] ||
      {};

    return {
      sequenceIndex,
      term: card.term || termName,
      definition: card.definition || '',
      exampleSentence: card.exampleSentence || '',
      relatedTerms: card.relatedTerms || [],
      learningSteps,
      learning: {
        tutorScript: primaryLearningStep.tutorScript || '',
        tutorAudioId: primaryLearningStep.tutorAudioId || null,
        stepIndex: primaryLearningStep.stepIndex ?? 0,
      },
      workbook: {
        tutorOpenWorkbook: wb.tutorOpenWorkbook || '',
        tutorTransition: wb.tutorTransition ?? primaryWorkbookExercise.tutorTransition ?? null,
        tutorOpenWorkbookAudioId: buildTermAudioId(
          sequenceIndex,
          'workbook.tutorOpenWorkbook',
        ),
        tutorTransitionAudioId: wb.tutorTransition
          ? buildTermAudioId(sequenceIndex, 'workbook.tutorTransition')
          : primaryWorkbookExercise.tutorTransitionAudioId || null,
        exercises: workbookExercises,
        primaryExerciseIndex: Math.max(
          0,
          workbookExercises.findIndex(exercise => exercise.isPrimary),
        ),
        exerciseLabel: primaryWorkbookExercise.exerciseLabel || '',
        exerciseNumber: primaryWorkbookExercise.exerciseNumber || 0,
        tutorReadExercise: primaryWorkbookExercise.tutorReadExercise || '',
        tutorPresentOptions: primaryWorkbookExercise.tutorPresentOptions || '',
        exercise: primaryWorkbookExercise.exercise || '',
        options: primaryWorkbookExercise.options || [],
        correctAnswer: primaryWorkbookExercise.correctAnswer || '',
        optionFeedback: primaryWorkbookExercise.optionFeedback || [],
        tutorReadExerciseAudioId:
          primaryWorkbookExercise.tutorReadExerciseAudioId || null,
        tutorPresentOptionsAudioId:
          primaryWorkbookExercise.tutorPresentOptionsAudioId || null,
        optionFeedbackAudioIds:
          primaryWorkbookExercise.optionFeedbackAudioIds || [],
      },
      knowledgeCheck: {
        tutorTransition: kc.tutorTransition ?? wb.tutorTransition ?? null,
        tutorTransitionAudioId: kc.tutorTransition
          ? buildTermAudioId(sequenceIndex, 'knowledgeCheck.tutorTransition')
          : wb.tutorTransition
          ? buildTermAudioId(sequenceIndex, 'workbook.tutorTransition')
          : null,
        questions: (kc.questions || []).map(mapQuestion),
      },
    };
  });

  return {
    metadata: {
      lessonId: metadata.lessonId || '',
      grade: metadata.grade || 0,
      subject: metadata.subject || '',
      topic: metadata.topic || '',
      lexileLevel: metadata.lexileLevel || '',
      lexileBand: metadata.lexileBand || '',
      estimatedDurationMinutes: metadata.estimatedDurationMinutes || 0,
      keyTermCount: metadata.keyTermCount || 0,
    },
    frontloading: {
      tutorScript: frontloading.tutorScript || '',
      tutorAudioId: buildFrontloadingTutorAudioId(),
      termOrder,
    },
    terms,
    mastery: {
      passingThreshold: masteryTest.passingThreshold ?? 0.8,
      questions: (masteryTest.questions || []).map(mapQuestion),
    },
    guidedNotes: {
      learningObjectives: guidedNotes.learningObjectives || [],
      keyTakeaways: guidedNotes.keyTakeaways || [],
    },
  };
}
